import { useMarketingEntryCta } from "@/hooks/use-marketing-entry-cta"
import { cn } from "cn"

import { useTranslation } from "@/lib/i18n/i18n-context"
import { isEuRegion } from "@/lib/region"
import { NO_TRANSLATE } from "@/lib/weglot"
import { Button, type ButtonProps } from "@/components/ui/button"

type MarketingEntryCtaButtonProps = Omit<
  ButtonProps,
  "onClick" | "href" | "disabled"
> & {
  /** Provider from the server render, before the client session hydrates. */
  initialProvider?: string
  setupLabel?: string
  onClick?: (action: "setup" | "get_started") => void
}

export function MarketingEntryCtaButton({
  initialProvider,
  setupLabel,
  onClick,
  children,
  ...buttonProps
}: MarketingEntryCtaButtonProps) {
  const t = useTranslation()
  const cta = useMarketingEntryCta({ initialProvider })
  if (isEuRegion()) return null
  const labels = (
    <>
      <span className={cn("inline-grid", NO_TRANSLATE)} data-slot="setup-label">
        <span
          className={cn(
            "col-start-1 row-start-1",
            cta.state !== "setup" && "invisible"
          )}
        >
          {setupLabel ?? t("marketingCtaSetup")}
        </span>
        <span
          className={cn(
            "col-start-1 row-start-1",
            cta.state !== "get-started" && "invisible"
          )}
        >
          {t("marketingCtaGetStarted")}
        </span>
      </span>
      {children}
    </>
  )

  if (cta.state === "loading") {
    return (
      <Button
        {...buttonProps}
        loading
        aria-busy="true"
        aria-label={t("loading")}
      >
        {labels}
      </Button>
    )
  }

  if (cta.state === "setup") {
    return (
      <Button
        {...buttonProps}
        loading={cta.setupDisabled}
        onClick={() => {
          onClick?.("setup")
          cta.onSetup()
        }}
      >
        {labels}
      </Button>
    )
  }

  return (
    <Button
      {...buttonProps}
      href={cta.href}
      onClick={() => onClick?.("get_started")}
    >
      {labels}
    </Button>
  )
}
